import type { ReactNode } from 'react';
import { Cake, type LucideIcon, Package, UserPlus, Wallet } from 'lucide-react';
import { Link } from 'react-router';
import {
  type DashboardAgenda,
  type DashboardFinance,
  formatCentsBRL,
  type PackagesSummaryDto,
} from '@brimostech/shared';
import { Button } from '@brimostech/ui';
import { Section } from '@/components/section';
import { formatRelative } from '@/lib/format';

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The day in small human facts (ADR-111): who is expected, who celebrates, what waits. */
export function AgendaSection({
  agenda,
  packages,
  finance,
}: {
  agenda: DashboardAgenda;
  packages: PackagesSummaryDto | null;
  finance: DashboardFinance | null;
}) {
  const celebrating = agenda.birthdaysToday;
  return (
    <Section title="Hoje" description="Quem chega, quem faz aniversário e o que está esperando.">
      <ul className="space-y-4 text-sm">
        <Row
          icon={UserPlus}
          title={
            agenda.visitorsExpected === 0
              ? 'Nenhum visitante esperado'
              : plural(agenda.visitorsExpected, 'visitante esperado', 'visitantes esperados')
          }
          detail="convites de morador válidos para hoje"
          to="/visitantes"
        />
        <Row
          icon={Cake}
          title={
            celebrating.length === 0
              ? 'Sem aniversários hoje'
              : `Aniversário hoje: ${celebrating.map((b) => b.name).join(', ')}`
          }
          detail={
            celebrating.length > 0
              ? celebrating
                  .map((b) => b.unitLabel)
                  .filter(Boolean)
                  .join(', ') || 'sem unidade vinculada'
              : agenda.birthdaysThisMonth === 0
                ? 'nenhum este mês'
                : `${plural(agenda.birthdaysThisMonth, 'aniversariante', 'aniversariantes')} este mês`
          }
        />
        {packages ? (
          <Row
            icon={Package}
            title={
              packages.pending === 0
                ? 'Nenhuma encomenda na portaria'
                : plural(packages.pending, 'encomenda aguardando', 'encomendas aguardando')
            }
            detail={
              packages.pending === 0
                ? 'tudo entregue'
                : [
                    packages.stale > 0 ? `${packages.stale} há mais de 3 dias` : null,
                    packages.perishable > 0 ? `${packages.perishable} perecível(is)` : null,
                    packages.oldestReceivedAt
                      ? `a mais antiga chegou ${formatRelative(packages.oldestReceivedAt)}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'todas recentes'
            }
            to="/encomendas"
          />
        ) : null}
        {finance ? (
          <Row
            icon={Wallet}
            title={
              finance.unitsOverdue === 0
                ? 'Nenhuma unidade em atraso'
                : plural(finance.unitsOverdue, 'unidade em atraso', 'unidades em atraso')
            }
            detail={`${formatCentsBRL(finance.overdueCents)} vencidos · ${formatCentsBRL(finance.openCents)} a vencer no mês`}
            to="/financeiro"
          />
        ) : null}
      </ul>
    </Section>
  );
}

function Row({
  icon: Icon,
  title,
  detail,
  to,
}: {
  icon: LucideIcon;
  title: ReactNode;
  detail: ReactNode;
  to?: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/8 text-primary">
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{detail}</div>
      </div>
      {to ? (
        <Button variant="ghost" size="sm" asChild>
          <Link to={to}>Abrir</Link>
        </Button>
      ) : null}
    </li>
  );
}
